<script setup lang="ts">
import type { Schemas } from '#shopware'
import { getCategoryRoute } from '@shopware/helpers'
import { useClipboard } from '@vueuse/core'
import MiscLink from '~/components/misc/MiscLink.vue'
import StructureElementHeroSliderBanderoleCountdown from '~/components/structure/element/StructureElementHeroSliderBanderoleCountdown.vue'
import type { PromotionData } from '~/composables/usePromotion'

const props = defineProps<{
    content: Schemas['CmsElementHeroBanner']
}>()

const getConfig = computed(() => props.content?.config ?? null)

const hasValidButtonConfig = computed(() => {
    return hasContent(getConfig.value?.buttonText?.value) && hasContent(getConfig.value?.buttonTarget?.value)
})

const backgroundColor = computed(() => getConfig.value?.backgroundColor?.value ?? null)
const backgroundColorDarker = computed(() => getConfig.value?.backgroundColorDarker?.value ?? null)

const promotionStyle = computed(() => {
    if (backgroundColor.value) {
        return { backgroundColor: backgroundColor.value }
    }
    return {}
})

const promotionButtonStyle = computed(() => {
    if (backgroundColor.value) {
        return { color: backgroundColor.value }
    }
    return {}
})

const categoriesContainerStyle = computed(() => {
    if (backgroundColorDarker.value) {
        return { backgroundColor: backgroundColorDarker.value }
    }
    return {}
})

const categories = computed(() => {
    const categoriesValue = props.content?.data?.categories
    if (Array.isArray(categoriesValue)) {
        return categoriesValue
            .filter(cat => cat?.categoryName != null && cat?.category != null)
            .map(cat => ({
                name: cat.categoryName!,
                url: getCategoryRoute(cat.category!)
            }))
    }
    return []
})

const hasCountdown = computed(() => hasContent(getConfig.value?.countdownEnd?.value))

const hasTitleOnly = computed(() => getConfig.value?.title?.value && !getConfig.value?.subtitle?.value)

const showConditionsButton = computed(() => getConfig.value?.showConditions?.value && hasContent(getConfig.value?.conditionsContent?.value))

function hasContent(input: string | null | undefined): input is string {
    return input != null && input.trim() !== ''
}

const promotionCodeRef = ref<HTMLElement | null>(null)
const { copy, copied, isSupported } = useClipboard()

function copyPromotionCode() {
    const text = promotionCodeRef.value?.innerText
    if (text && isSupported.value) {
        copy(text)
    }
}

const conditionsModalState = ref(false)

const heroBannerEl = ref<HTMLDivElement | null>(null)
const trackingData: PromotionData | undefined = props.content?.config?.promotion?.value ?? undefined
const { selectPromotion, computedPromotionData } = usePromotionObserver(heroBannerEl, trackingData)

function onPromotionClick(event: MouseEvent) {
    if (computedPromotionData?.value) selectPromotion(computedPromotionData.value, event)
}

const categoriesScrollContainer = ref<HTMLDivElement | null>(null)
const showScrollIndicator = ref(false)

function checkScrollable() {
    if (categoriesScrollContainer.value) {
        const { scrollWidth, clientWidth, scrollLeft } = categoriesScrollContainer.value
        const isScrollable = scrollWidth > clientWidth
        const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1
        showScrollIndicator.value = isScrollable && !isAtEnd
    }
}

watch(categories, () => {
    if (categories.value.length > 0) {
        nextTick(() => {
            // Use setTimeout to ensure DOM has fully rendered and layout is complete
            setTimeout(() => {
                checkScrollable()
            }, 100)
        })
    }
}, { immediate: true })

onMounted(() => {
    categoriesScrollContainer.value?.addEventListener('scroll', checkScrollable)
    window.addEventListener('resize', checkScrollable)
})

onUnmounted(() => {
    categoriesScrollContainer.value?.removeEventListener('scroll', checkScrollable)
    window.removeEventListener('resize', checkScrollable)
})
</script>

<template>
    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions vuejs-accessibility/click-events-have-key-events -->
    <div
        ref="heroBannerEl"
        @click="onPromotionClick($event)"
    >
        <div
            class="flex flex-col min-h-[180px]"
            :style="promotionStyle"
        >
            <!-- Header Container -->
            <div class="grid grid-cols-4 sm:grid-cols-8 md:grid-cols-12 md:gap-x-6 w-full max-w-7xl mx-auto px-4 md:px-6 pt-6">
                <div class="col-span-4 sm:col-span-6">
                    <!-- Header Content -->
                    <div class="flex flex-col">
                        <div
                            v-if="hasContent(getConfig?.title?.value)"
                            class="font-fjalla-one text-base-100 uppercase"
                            :class="[getConfig?.subtitle?.value ? 'text-6.5xl md:text-8xl' : 'text-5xl md:text-6.5xl']"
                        >
                            {{ getConfig?.title?.value }}
                        </div>
                        <div
                            v-if="hasContent(getConfig?.subtitle?.value)"
                            class="text-base-100"
                            :class="[getConfig?.title?.value ? 'text-lg pt-2' : 'text-xl md:text-2xl']"
                        >
                            {{ getConfig?.subtitle?.value }}
                        </div>
                        <div
                            v-if="hasContent(getConfig?.promotionCode?.value)"
                            class="flex flex-col gap-1 text-base-100 text-sm w-fit mt-4"
                        >
                            <span class="text-xxs">{{ $t('structure.slot.heroBanner.promotion.code') }}</span>
                            <div
                                role="button"
                                tabindex="0"
                                data-promo-click-type="copy_voucher_code"
                                class="font-semibold text-lg flex items-center gap-1 cursor-pointer tooltip tooltip-top"
                                :data-tip="copied ? `${$t('structure.slot.heroBanner.promotion.codeCopied')} ✓` : $t('structure.slot.heroBanner.promotion.copyCode')"
                                @click.prevent="copyPromotionCode"
                                @keydown.prevent.enter="copyPromotionCode"
                            >
                                <span ref="promotionCodeRef">{{ getConfig?.promotionCode?.value }}</span>
                                <MiscIcon name="clipboard" />
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Conditions Button mobile up -->
                <div class="md:col-span-4 md:col-end-13 lg:col-span-3 lg:col-end-13 xl:col-span-2 xl:col-end-13">
                    <button
                        v-if="getConfig?.showConditions?.value && hasContent(getConfig?.conditionsContent?.value)"
                        type="button"
                        data-promo-click-type="info"
                        class="hidden md:flex w-full items-center justify-center gap-[6px] p-2 md:px-3 md:py-1 rounded-full bg-base-100/10 text-base-100 text-sm cursor-pointer"
                        @click.prevent="conditionsModalState = true"
                        @keydown.prevent.enter="conditionsModalState = true"
                    >
                        <MiscIcon name="info" />
                        <span class="hidden md:block">{{ $t('structure.slot.heroBanner.promotion.conditions') }}</span>
                    </button>
                </div>
            </div>

            <!-- Button and Countdown Container -->
            <div class="w-full md:mb-8 mt-8" :class="[hasTitleOnly ? 'mb-5' : 'mb-4']">
                <div
                    class="flex md:flex-row md:justify-between max-w-7xl mx-auto px-4 md:px-6 gap-6 md:gap-0"
                    :class="[showConditionsButton && !hasCountdown ? 'justify-between' : 'flex-col items-start']"
                >
                    <MiscLink
                        v-if="hasValidButtonConfig"
                        :to="getConfig?.buttonTarget?.value"
                        class="btn px-[14px] py-[10px] btn-light btn-icon-right !rounded-xs"
                        :style="promotionButtonStyle"
                    >
                        {{ getConfig?.buttonText?.value }}
                        <MiscIcon name="arrow-right" />
                    </MiscLink>

                    <div
                        v-if="hasCountdown || showConditionsButton"
                        class="flex justify-between md:w-auto items-center text-xs text-base-100"
                        :class="[!hasCountdown ? '' : 'w-full']"
                    >
                        <div v-if="hasCountdown" class="flex flex-col gap-1">
                            <div class="text-xxs text-base-100">{{ $t('structure.slot.heroBanner.promotion.countdownTitle') }}</div>
                            <StructureElementHeroSliderBanderoleCountdown
                                v-if="getConfig?.countdownEnd?.value != null"
                                :date="getConfig.countdownEnd.value"
                                type="C"
                            />
                        </div>
                        <!-- Conditions Button mobile only -->
                        <button
                            v-if="showConditionsButton"
                            type="button"
                            data-promo-click-type="info"
                            class="flex md:hidden items-center gap-1 p-2 rounded-full bg-base-100/10 text-base-100 text-xs cursor-pointer"
                            @click.prevent="conditionsModalState = true"
                            @keydown.prevent.enter="conditionsModalState = true"
                        >
                            <MiscIcon name="info" />
                        </button>
                    </div>
                </div>
            </div>

            <!-- Categories Container -->
            <div
                v-if="categories.length > 0"
                class="w-full"
                :style="categoriesContainerStyle"
            >
                <div class="max-w-7xl mx-auto px-4 py-4 md:px-6 flex flex-col gap-2">
                    <div class="text-xxs text-base-100">
                        {{ $t('structure.slot.heroBanner.promotion.categoriesTitle') }}
                    </div>
                    <div class="relative">
                        <div
                            ref="categoriesScrollContainer"
                            class="flex gap-2 overflow-x-auto overflow-y-hidden scrollbar-hide"
                        >
                            <template v-for="(category, index) in categories" :key="index">
                                <MiscLink
                                    :to="category.url"
                                    class="flex items-center text-base text-base-100 font-semibold whitespace-nowrap flex-shrink-0 hover:opacity-75"
                                >
                                    <span>{{ category.name }}</span>
                                </MiscLink>
                                <span v-if="index <= categories.length" class="mx-4 text-base-100">|</span>
                            </template>

                            <MiscLink
                                v-if="hasContent(getConfig?.showAllLinkTarget?.value)"
                                :to="getConfig?.showAllLinkTarget?.value"
                                class="text-base text-base-100 font-semibold whitespace-nowrap flex-shrink-0"
                            >
                                {{ $t('structure.slot.heroBanner.promotion.showAll') }}
                            </MiscLink>
                        </div>
                        <!-- Scroll Indicator -->
                        <div
                            v-if="showScrollIndicator"
                            class="absolute right-0 top-0 bottom-0 w-32 pointer-events-none flex items-center justify-end pr-2"
                            :style="{ background: `linear-gradient(to right, transparent 0%, ${backgroundColorDarker || backgroundColor || '#000'} 70%)` }"
                        >
                            <MiscIcon name="chevron-right" class="text-base-100" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <!-- Conditions Modal -->
    <template v-if="getConfig?.showConditions?.value && hasContent(getConfig?.conditionsContent?.value)">
        <input
            id="promotion-conditions-modal"
            v-model="conditionsModalState"
            type="checkbox"
            class="modal-toggle"
        >
        <div class="modal modal-top sm:modal-middle">
            <div class="modal-box relative max-w-2xl">
                <label
                    role="button"
                    tabindex="0"
                    for="promotion-conditions-modal"
                    class="btn btn-sm btn-circle absolute right-2 top-2"
                    @click.prevent="conditionsModalState = false"
                    @keydown.enter="conditionsModalState = false"
                >
                    <MiscIcon name="x" class="h-4 w-4" />
                </label>
                <h3 class="font-semibold text-lg mb-4 text-center">
                    {{ $t('structure.slot.heroBanner.promotion.conditionsTitle') }}
                </h3>
                <div class="prose max-w-none">
                    {{ getConfig?.conditionsContent?.value }}
                </div>
                <button
                    type="button"
                    class="btn w-full btn-md btn-primary mt-12"
                    @click.prevent="conditionsModalState = false"
                    @keydown.prevent.enter="conditionsModalState = false"
                >
                    {{ $t('structure.slot.heroBanner.promotion.close') }}
                </button>
            </div>
            <label tabindex="0" class="modal-backdrop cursor-pointer" for="promotion-conditions-modal">
                {{ $t('structure.slot.heroBanner.promotion.close') }}
            </label>
        </div>
    </template>
</template>
